// NetworkToFoundationDiagram — direct port of network-to-foundation-preview-3.html.
// Driven by a parent `progress` MotionValue (0 → 1). All per-frame work happens
// inside a single requestAnimationFrame loop that mutates a cached <g> via
// innerHTML — no React reconciliation per frame.
import { useEffect, useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useTransform, type MotionValue } from "framer-motion";
import { Layers, ArrowRight, ShieldCheck, type LucideIcon } from "lucide-react";

// ============================================================
// CONSTANTS — ported verbatim from the HTML
// ============================================================
const VB_W = 1000;
const VB_H = 660;
const BAR_Y = 20;
const BAR_H = 266;
const BAR_X = 80;
const BAR_W = VB_W - BAR_X * 2;

const PILL_HEIGHT = 28;
const ROW1_Y = 500;
const ROW2_Y = 588;
const pillWidth = (label: string) =>
  Math.min(195, Math.max(95, label.length * 6.5 + 24));




type ChaosPos = { label: string; cx: number; cy: number };
type Node = ChaosPos & { ox: number; oy: number };

const CHAOS_POSITIONS: ChaosPos[] = [
  { label: "Commercial AI workflow",        cx: 170, cy: 115 },
  { label: "Clinical triage workflow",      cx: 530, cy: 140 },
  { label: "In-house AI workflow",          cx: 320, cy: 245 },
  { label: "Coding & billing agent",        cx: 720, cy: 210 },
  { label: "Clinical documentation agent",  cx: 200, cy: 345 },
  { label: "Reporting QA workflow",         cx: 600, cy: 305 },
  { label: "Trial matching agent",          cx: 400, cy: 410 },
  { label: "Incidentals workflow", cx: 760, cy: 375 },
];

function computeRowOrdered(items: ChaosPos[], y: number): Node[] {
  const padding = BAR_X;
  const usable = VB_W - 2 * padding;
  const stepSize = usable / items.length;
  return items.map((it, i) => {
    const ox = padding + stepSize * (i + 0.5);
    return { ...it, ox, oy: y };
  });
}

const NODES: Node[] = [
  ...computeRowOrdered(CHAOS_POSITIONS.slice(0, 4), ROW1_Y),
  ...computeRowOrdered(CHAOS_POSITIONS.slice(4, 8), ROW2_Y),
];

console.assert(
  NODES.length === 8 &&
    NODES.every((n) => Number.isFinite(n.ox) && Number.isFinite(n.oy)),
  "NODES must have ox/oy computed via computeRowOrdered"
);

const PILL_LINKS: Array<[number, number]> = [
  [0, 1],
  [0, 4],
  [1, 6],
  [1, 7],
  [6, 7],
  [4, 5],
  [2, 5],
  [3, 5],
];

type NoiseLabel = { label: string; x: number; y: number; alert?: boolean };

const NOISE_LABELS: NoiseLabel[] = [
  { label: "EHR",                       x: 240, y: 60  },
  { label: "FHIR",                      x: 500, y: 255 },
  { label: "PACS",                      x: 780, y: 90  },
  { label: "RCM",                       x: 60,  y: 280 },
  { label: "Failed routing",            x: 840, y: 260, alert: true },
  { label: "Clinical data mapping",     x: 200, y: 200 },
  { label: "Performance drift",         x: 520, y: 50,  alert: true },
  { label: "Data access",               x: 875, y: 410, alert: true },
  { label: "Cybersecurity review",      x: 100, y: 400 },
  { label: "Governance approval",       x: 800, y: 470 },
  { label: "Clinical validation",       x: 260, y: 480 },
  { label: "ROI tracking",              x: 440, y: 500, alert: true },
  { label: "Vendor onboarding",         x: 620, y: 480 },
];

const LABELS_BELOW = new Set([10, 11, 12]);

const NOISE_DOTS: Array<{ x: number; y: number }> = [];
const NOISE_COUNT = NOISE_LABELS.length + NOISE_DOTS.length;

const NOISE_LINKS: Array<[number, number]> = [
  [0, 5],
  [5, 3],
  [3, 8],
  [8, 10],
  [10, 11],
  [11, 12],
  [12, 9],
  [9, 7],
  [7, 4],
  [4, 2],
  [2, 6],
  [1, 6],
  [1, 5],
  [6, 0],
  [11, 2],
  [7, 1],
];

const PILL_DEPTHS: number[] = Array.from(
  { length: 8 },
  (_, i) => ((i * 29 + 7) % 100) / 100
);

type DepthItem =
  | { kind: "noise-line"; a: number; b: number; depth: number; idx: number }
  | { kind: "pill-line"; a: number; b: number; depth: number; idx: number }
  | { kind: "noise-label"; idx: number; depth: number };

const ALL_DEPTH_ITEMS: DepthItem[] = (() => {
  const items: DepthItem[] = [];
  NOISE_LINKS.forEach(([a, b], i) => {
    const depth = ((a * 7 + b * 13 + i * 5) % 100) / 100;
    items.push({ kind: "noise-line", a, b, depth, idx: i });
  });
  PILL_LINKS.forEach(([a, b], i) => {
    const depth = ((a * 11 + b * 17 + i * 3) % 100) / 100;
    items.push({ kind: "pill-line", a, b, depth, idx: i });
  });
  NOISE_LABELS.forEach((_, i) => {
    const depth = ((i * 19 + 13) % 100) / 100;
    items.push({ kind: "noise-label", idx: i, depth });
  });
  items.sort((x, y) => x.depth - y.depth);
  return items;
})();

// ============================================================
// MATH HELPERS
// ============================================================
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smoothstep = (a: number, b: number, x: number) => {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};
const rangeMap = (p: number, s: number, e: number, fv: number, tv: number) =>
  lerp(fv, tv, smoothstep(s, e, p));
function fourStopOpacity(
  p: number,
  s1: number,
  s2: number,
  s3: number,
  s4: number,
  mid: number
) {
  if (p < s1) return 0;
  if (p < s2) return lerp(0, mid, (p - s1) / (s2 - s1));
  if (p < s3) return mid;
  if (p < s4) return lerp(mid, 0, (p - s3) / (s4 - s3));
  return 0;
}

// Phase constants (from HTML)
const NOISE_IN_START = -0.075;
const NOISE_IN_END = 0.075;
const PILL_APPEAR_START_BASE = -0.10;
const PILL_APPEAR_SPREAD = 0.22;
const PILL_WEB_START = -0.06;
const PILL_WEB_END = 0.06;
const WEB_FADE_OUT_START = 0.36;
const WEB_FADE_OUT_END = 0.44;
const MIGRATE_START = 0.38;
const MIGRATE_END = 0.50;
const CONV_START = 0.50;
const BAR_START = 0.42;
const TITLE_START = 0.46;
const COL_START = 0.48;

const COLS = [
  {
    name: "BUILD",
    title: ["Package AI capabilities."],
    desc: ["Build, buy, or validate."],
  },
  {
    name: "DEPLOY",
    title: ["Into existing workflows."],
    desc: ["Where teams already work."],
  },
  {
    name: "GOVERN",
    title: ["Monitor every workflow."],
    desc: ["One evidence trail."],
  },
];

// ============================================================
// RENDER HELPERS
// ============================================================
function noiseWobble(idx: number, time: number) {
  const base =
    idx < NOISE_LABELS.length
      ? NOISE_LABELS[idx]
      : NOISE_DOTS[idx - NOISE_LABELS.length];
  if (!base) return null;
  const phase = idx * 1.37;
  return {
    x: base.x + Math.sin(time * 0.6 + phase) * 8,
    y: base.y + Math.cos(time * 0.5 + phase * 1.7) * 5.5,
  };
}

function pillChaosPos(i: number, progress: number, time: number) {
  const n = NODES[i];
  const wobbleAmount = 1 - smoothstep(MIGRATE_START, MIGRATE_END, progress);
  const wPhase = i * 1.9;
  const wobX =
    wobbleAmount > 0.001
      ? Math.sin(time * 0.6 + wPhase) * 8 * wobbleAmount
      : 0;
  const wobY =
    wobbleAmount > 0.001
      ? Math.cos(time * 0.5 + wPhase * 1.7) * 5.5 * wobbleAmount
      : 0;
  return {
    x: rangeMap(progress, MIGRATE_START, MIGRATE_END, n.cx, n.ox) + wobX,
    y: rangeMap(progress, MIGRATE_START, MIGRATE_END, n.cy, n.oy) + wobY,
  };
}

function findPillLinkIdx(from: number, to: number): number {
  for (let i = 0; i < PILL_LINKS.length; i++) {
    const [a, b] = PILL_LINKS[i];
    if ((a === from && b === to) || (a === to && b === from)) return i;
  }
  return -1;
}

// ============================================================
// RENDER — builds full inner-SVG string for a given (progress, time)
// ============================================================
function renderSVG(progress: number, time: number, reduce: boolean): string {
  const wobbleTime = reduce ? 0 : time;
  let svg = "";

  const noiseOp = fourStopOpacity(
    progress,
    NOISE_IN_START,
    NOISE_IN_END,
    WEB_FADE_OUT_START,
    WEB_FADE_OUT_END,
    0.85
  );
  const pillWebOp = fourStopOpacity(
    progress,
    PILL_WEB_START,
    PILL_WEB_END,
    WEB_FADE_OUT_START,
    WEB_FADE_OUT_END,
    1.0
  );

  function renderDepthItem(item: DepthItem): string {
    const d = item.depth;
    const isFront = d >= 0.5;
    const opMul = isFront ? 0.95 + (d - 0.5) * 0.3 : 0.4 + d * 0.5;
    const LINE_WIDTH = 1.3;

    if (item.kind === "noise-line") {
      if (noiseOp <= 0.001) return "";
      if (item.a >= NOISE_COUNT || item.b >= NOISE_COUNT) return "";
      const pa = noiseWobble(item.a, wobbleTime);
      const pb = noiseWobble(item.b, wobbleTime);
      if (!pa || !pb) return "";
      const lineDelay = (item.idx / NOISE_LINKS.length) * 0.08;
      const drawStart = NOISE_IN_START + lineDelay;
      const drawEnd = drawStart + 0.06;
      const drawn = smoothstep(drawStart, drawEnd, progress);
      const dashLen = Math.hypot(pa.x - pb.x, pa.y - pb.y) + 50;
      const finalOp = 0.55 * noiseOp * opMul;
      return `<path d="M ${pa.x.toFixed(2)} ${pa.y.toFixed(2)} L ${pb.x.toFixed(2)} ${pb.y.toFixed(2)}" fill="none" stroke="hsl(var(--muted-foreground))" stroke-opacity="${finalOp.toFixed(3)}" stroke-width="${LINE_WIDTH.toFixed(2)}" stroke-dasharray="${dashLen.toFixed(2)}" stroke-dashoffset="${(dashLen * (1 - drawn)).toFixed(2)}" />`;
    }

    if (item.kind === "pill-line") {
      if (pillWebOp <= 0.001) return "";
      if (item.a >= NODES.length || item.b >= NODES.length) return "";
      const pa = pillChaosPos(item.a, progress, wobbleTime);
      const pb = pillChaosPos(item.b, progress, wobbleTime);
      const lineDelay = (item.idx / PILL_LINKS.length) * 0.06;
      const drawStart = PILL_WEB_START + lineDelay;
      const drawEnd = drawStart + 0.05;
      const drawn = smoothstep(drawStart, drawEnd, progress);
      const midX = (pa.x + pb.x) / 2;
      const midY = (pa.y + pb.y) / 2;
      const dx = pb.x - pa.x;
      const dy = pb.y - pa.y;
      const len = Math.hypot(dx, dy) || 1;
      const side = item.idx % 2 === 0 ? 1 : -1;
      const offset = Math.min(60, len * 0.28);
      const ctrlX = midX + (-dy / len) * offset * side;
      const ctrlY = midY + (dx / len) * offset * side;
      const dashLen = len * 1.2 + 80;
      const finalOp = 0.6 * pillWebOp;
      const finalWidth = LINE_WIDTH * 2.5;
      return `<path d="M ${pa.x.toFixed(2)} ${pa.y.toFixed(2)} Q ${ctrlX.toFixed(2)} ${ctrlY.toFixed(2)} ${pb.x.toFixed(2)} ${pb.y.toFixed(2)}" fill="none" stroke="hsl(var(--primary))" stroke-opacity="${finalOp.toFixed(3)}" stroke-width="${finalWidth.toFixed(2)}" stroke-dasharray="${dashLen.toFixed(2)}" stroke-dashoffset="${(dashLen * (1 - drawn)).toFixed(2)}" stroke-linecap="round" />`;
    }

    // noise-label
    if (noiseOp <= 0.001) return "";
    const i = item.idx;
    const n = NOISE_LABELS[i];
    const lblDelay = (i / NOISE_LABELS.length) * 0.08;
    const lblOp = smoothstep(
      NOISE_IN_START + lblDelay + 0.02,
      NOISE_IN_START + lblDelay + 0.07,
      progress
    );
    const pos = noiseWobble(i, wobbleTime)!;
    const fontSize = 12;
    const dotR = 3;
    let out = `<g opacity="${(lblOp * noiseOp).toFixed(3)}">`;
    const labelFill = n.alert ? "hsl(32 55% 42%)" : "hsl(var(--muted-foreground))";
    if (n.alert) {
      out += `<circle cx="${pos.x.toFixed(2)}" cy="${pos.y.toFixed(2)}" r="6.5" fill="hsl(32 95% 55%)" fill-opacity="0.95" stroke="white" stroke-opacity="0.9" stroke-width="1" />`;
      out += `<text x="${pos.x.toFixed(2)}" y="${pos.y.toFixed(2)}" text-anchor="middle" dominant-baseline="central" font-size="8" font-family="Inter, system-ui, sans-serif" font-weight="700" fill="white">!</text>`;
    } else {
      out += `<circle cx="${pos.x.toFixed(2)}" cy="${pos.y.toFixed(2)}" r="${dotR.toFixed(2)}" fill="hsl(var(--muted-foreground))" />`;
    }
    if (LABELS_BELOW.has(i)) {
      const textY = pos.y + (n.alert ? 6 : dotR) + fontSize * 1.0 + 2;
      out += `<text x="${pos.x.toFixed(2)}" y="${textY.toFixed(2)}" text-anchor="middle" font-size="${fontSize.toFixed(1)}" font-family="Inter, system-ui, sans-serif" font-weight="500" fill="${labelFill}" letter-spacing="0.5">${n.label}</text>`;
    } else {
      const textX = pos.x + (n.alert ? 12 : 10);
      const textY = pos.y + 4;
      out += `<text x="${textX.toFixed(2)}" y="${textY.toFixed(2)}" font-size="${fontSize.toFixed(1)}" font-family="Inter, system-ui, sans-serif" font-weight="500" fill="${labelFill}" letter-spacing="0.5">${n.label}</text>`;
    }
    out += `</g>`;
    return out;
  }

  // LAYER 1: ALL grey lines
  for (const item of ALL_DEPTH_ITEMS) {
    if (item.kind === "noise-line") svg += renderDepthItem(item);
  }

  // CARS on the GREY WEB — constant lap time
  if (noiseOp > 0.001) {
    const buildGreyCar = (tour: number[], lapSec: number, phase: number): string => {
      const startPos = noiseWobble(tour[0], wobbleTime);
      if (!startPos) return "";
      let d = `M ${startPos.x.toFixed(2)} ${startPos.y.toFixed(2)}`;
      let pathLen = 0;
      for (let i = 1; i < tour.length; i++) {
        const prev = noiseWobble(tour[i - 1], wobbleTime);
        const curr = noiseWobble(tour[i], wobbleTime);
        if (!prev || !curr) continue;
        d += ` L ${curr.x.toFixed(2)} ${curr.y.toFixed(2)}`;
        pathLen += Math.hypot(curr.x - prev.x, curr.y - prev.y);
      }
      if (pathLen <= 0) return "";
      const segLen = 50;
      const lapProgress = ((((time + phase) % lapSec) + lapSec) % lapSec) / lapSec;
      const dashOffset = -lapProgress * pathLen;
      const dashArray = `${segLen} ${(pathLen - segLen).toFixed(2)}`;
      return `<path d="${d}" fill="none" stroke="hsl(var(--muted-foreground))" stroke-opacity="${noiseOp.toFixed(3)}" stroke-width="3" stroke-linecap="round" stroke-dasharray="${dashArray}" stroke-dashoffset="${dashOffset.toFixed(2)}" />`;
    };
    if (!reduce) {
      svg += buildGreyCar([0, 5, 3, 8, 10, 11, 12, 9, 7, 4, 2, 6, 0], 54, 0);
      svg += buildGreyCar([6, 1, 5, 0, 6], 20, 1.0);
      svg += buildGreyCar([2, 11, 12, 9, 7, 4, 2], 30, 2.0);
      svg += buildGreyCar([1, 7, 4, 2, 6, 1], 27, 3.5);
    }
  }

  // LAYER 2: purple pill-lines + noise labels (depth-mixed)
  for (const item of ALL_DEPTH_ITEMS) {
    if (item.kind === "pill-line" || item.kind === "noise-label")
      svg += renderDepthItem(item);
  }

  // CARS on the PURPLE WEB
  const highlightOp = fourStopOpacity(
    progress,
    PILL_WEB_START + 0.05,
    PILL_WEB_END + 0.05,
    WEB_FADE_OUT_START,
    WEB_FADE_OUT_END,
    1.0
  );
  if (highlightOp > 0.001 && !reduce) {
    const buildCar = (tour: number[], lapSec: number, phase: number): string => {
      const startPos = pillChaosPos(tour[0], progress, wobbleTime);
      let d = `M ${startPos.x.toFixed(2)} ${startPos.y.toFixed(2)}`;
      let pathLen = 0;
      for (let i = 1; i < tour.length; i++) {
        const linkIdx = findPillLinkIdx(tour[i - 1], tour[i]);
        if (linkIdx < 0) continue;
        const [la, lb] = PILL_LINKS[linkIdx];
        const aPos = pillChaosPos(la, progress, wobbleTime);
        const bPos = pillChaosPos(lb, progress, wobbleTime);
        const dxAB = bPos.x - aPos.x;
        const dyAB = bPos.y - aPos.y;
        const linkLen = Math.hypot(dxAB, dyAB) || 1;
        const off = Math.min(60, linkLen * 0.28);
        const side = linkIdx % 2 === 0 ? 1 : -1;
        const midX = (aPos.x + bPos.x) / 2;
        const midY = (aPos.y + bPos.y) / 2;
        const ctrlX = midX + (-dyAB / linkLen) * off * side;
        const ctrlY = midY + (dxAB / linkLen) * off * side;
        const toPos = pillChaosPos(tour[i], progress, wobbleTime);
        d += ` Q ${ctrlX.toFixed(2)} ${ctrlY.toFixed(2)} ${toPos.x.toFixed(2)} ${toPos.y.toFixed(2)}`;
        pathLen += linkLen * 1.1;
      }
      if (pathLen <= 0) return "";
      const segLen = 100;
      const lapProgress = ((((time + phase) % lapSec) + lapSec) % lapSec) / lapSec;
      const dashOffset = -lapProgress * pathLen;
      const dashArray = `${segLen} ${(pathLen - segLen).toFixed(2)}`;
      return `<path d="${d}" fill="none" stroke="hsl(var(--primary))" stroke-opacity="${highlightOp.toFixed(3)}" stroke-width="5" stroke-linecap="round" stroke-dasharray="${dashArray}" stroke-dashoffset="${dashOffset.toFixed(2)}" />`;
    };
    svg += buildCar([0, 1, 6, 7, 1, 0, 4, 5, 4, 0], 42, 0);
    svg += buildCar([3, 5, 2, 5, 4, 5, 3], 22, 1.5);
  }

  // LAYER 3: Convergence lines (dashed reveal → flowing dashes)
  NODES.forEach((n, i) => {
    const start = CONV_START + (i / NODES.length) * 0.06;
    const end = start + 0.05;
    const t = NODES.length === 1 ? 0.5 : i / (NODES.length - 1);
    const sx = n.ox;
    const sy = n.oy - PILL_HEIGHT / 2;
    const ex = VB_W / 2 - 300 / 2 + t * 300;
    const ey = BAR_Y + BAR_H;
    const c1y = sy + (ey - sy) * 0.55;
    const c2y = ey - (ey - sy) * 0.25;
    const d = `M ${sx} ${sy} C ${sx} ${c1y}, ${ex} ${c2y}, ${ex} ${ey}`;
    const dashLen = 240;
    const drawOp = fourStopOpacity(progress, start, start + 0.02, end, end + 0.02, 0.85);
    const drawn = smoothstep(start, end, progress);
    if (drawOp > 0.001) {
      svg += `<path d="${d}" fill="none" stroke="url(#n2fConvergeFade)" stroke-width="1.4" stroke-linecap="round" opacity="${drawOp.toFixed(3)}" stroke-dasharray="${dashLen}" stroke-dashoffset="${(dashLen * (1 - drawn)).toFixed(2)}" />`;
    }
    const flowOp = smoothstep(end, end + 0.02, progress) * 0.85;
    if (flowOp > 0.001) {
      const flowOffset = -((wobbleTime * 26) + (i * 5)) % 24;
      svg += `<path d="${d}" fill="none" stroke="url(#n2fConvergeFade)" stroke-width="1.4" stroke-linecap="round" opacity="${flowOp.toFixed(3)}" stroke-dasharray="6 18" stroke-dashoffset="${flowOffset.toFixed(2)}" />`;
    }
  });

  // LAYER 4: PILLS — solid primary fill, primary-foreground text
  function renderPill(i: number): string {
    const n = NODES[i];
    const appearStart =
      PILL_APPEAR_START_BASE + (i / NODES.length) * PILL_APPEAR_SPREAD;
    const appearEnd = appearStart + 0.04;
    const op = smoothstep(appearStart, appearEnd, progress);
    if (op <= 0.001) return "";
    const pillDepth = PILL_DEPTHS[i] || 0.5;
    const isLarge = i === 0 || i === 7;
    // Force the two highlighted pills to render large during chaos
    // (their natural PILL_DEPTHS are ~0.1 which would otherwise shrink them).
    // They still lerp back to scale 1 during migration.
    const chaosScale = isLarge ? 1.25 : 0.55 + pillDepth * 0.95;
    const migration = smoothstep(MIGRATE_START, MIGRATE_END, progress);
    const depthScale = lerp(chaosScale, 1, migration);
    const scale = lerp(0.85, 1, op) * depthScale;
    const pos = pillChaosPos(i, progress, wobbleTime);
    const w = pillWidth(n.label);
    const h = PILL_HEIGHT;
    let out = `<g transform="translate(${pos.x.toFixed(2)},${pos.y.toFixed(2)}) scale(${scale.toFixed(3)})" opacity="${op.toFixed(3)}">`;
    out += `<rect x="${-w / 2}" y="${-h / 2}" width="${w}" height="${h}" rx="${h / 2}" fill="hsl(var(--primary))" stroke="hsl(var(--primary))" stroke-width="3.25" vector-effect="non-scaling-stroke" />`;
    out += `<text x="0" y="4" text-anchor="middle" font-size="12" font-family="Inter, system-ui, sans-serif" font-weight="500" fill="hsl(var(--primary-foreground))">${n.label}</text>`;



    out += `</g>`;
    return out;
  }

  NODES.forEach((_, i) => {
    svg += renderPill(i);
  });

  // LAYER 5: Foundation bar
  const barOp = smoothstep(BAR_START, BAR_START + 0.06, progress);
  if (barOp > 0.001) {
    const scaleX = rangeMap(progress, BAR_START, BAR_START + 0.08, 0.75, 1);
    const haloOp = smoothstep(BAR_START - 0.06, 1, progress) * 0.55;
    const headerOp = smoothstep(TITLE_START, TITLE_START + 0.06, progress);

    const headerH = 56;
    const headerY = BAR_Y + headerH;
    const dividerXs = [BAR_X + BAR_W * (2 / 6), BAR_X + BAR_W * (4 / 6)];
    const iconSize = 40;
    const iconY = headerY + 32;
    const nameY = iconY + iconSize + 26;
    const titleY = nameY + 28;

    let bar = "";
    bar += `<ellipse cx="${VB_W / 2}" cy="${BAR_Y + BAR_H / 2}" rx="${BAR_W / 2 + 80}" ry="${BAR_H / 2 + 40}" fill="url(#n2fCapsuleGlow)" opacity="${haloOp.toFixed(3)}" />`;
    bar += `<g transform="translate(${VB_W / 2}, ${BAR_Y + BAR_H / 2}) scale(${scaleX.toFixed(3)}, 1) translate(${-VB_W / 2}, ${-(BAR_Y + BAR_H / 2)})">`;
    bar += `<rect x="${BAR_X}" y="${BAR_Y}" width="${BAR_W}" height="${BAR_H}" rx="14" fill="hsl(var(--card))" stroke="hsl(var(--border))" stroke-width="1" />`;
    bar += `<rect x="${BAR_X}" y="${BAR_Y}" width="${BAR_W}" height="${headerH}" rx="14" fill="hsl(var(--muted) / 0.5)" />`;
    bar += `<rect x="${BAR_X}" y="${BAR_Y + 30}" width="${BAR_W}" height="${headerH - 30}" fill="hsl(var(--muted) / 0.5)" />`;
    bar += `<line x1="${BAR_X}" y1="${headerY}" x2="${BAR_X + BAR_W}" y2="${headerY}" stroke="hsl(var(--border))" stroke-width="1" />`;

    const pulseTime = reduce ? 0 : (wobbleTime * 0.5) % 1;
    const haloR = 4 + pulseTime * 6;
    const haloOpDot = (1 - pulseTime) * 0.35;
    bar += `<g opacity="${headerOp.toFixed(3)}">`;
    bar += `<circle cx="${BAR_X + 28}" cy="${BAR_Y + 28}" r="${haloR.toFixed(2)}" fill="hsl(var(--primary))" opacity="${haloOpDot.toFixed(3)}" />`;
    bar += `<circle cx="${BAR_X + 28}" cy="${BAR_Y + 28}" r="4" fill="hsl(var(--primary))" />`;
    bar += `</g>`;

    bar += `<text x="${BAR_X + 48}" y="${BAR_Y + 33}" font-size="13" font-family="Inter, system-ui, sans-serif" font-weight="700" fill="hsl(var(--primary))" letter-spacing="1.6" opacity="${headerOp.toFixed(3)}">deepcOS · CLINICAL AI INFRASTRUCTURE</text>`;
    bar += `<text x="${BAR_X + BAR_W - 28}" y="${BAR_Y + 33}" font-size="11" font-family="Inter, system-ui, sans-serif" font-weight="500" fill="hsl(var(--muted-foreground))" letter-spacing="1.4" text-anchor="end" opacity="${headerOp.toFixed(3)}">BUILD · DEPLOY · GOVERN</text>`;

    COLS.forEach((col, ci) => {
      const cardStart = COL_START + ci * 0.02;
      const cardEnd = cardStart + 0.06;
      const cardOp = smoothstep(cardStart, cardEnd, progress);
      if (cardOp <= 0.001) return;
      const colLeft = ci === 0 ? BAR_X : dividerXs[ci - 1];
      const tileX = colLeft + 24;

      bar += `<g opacity="${cardOp.toFixed(3)}">`;
      if (ci > 0) {
        const dx = dividerXs[ci - 1];
        bar += `<line x1="${dx}" x2="${dx}" y1="${headerY}" y2="${BAR_Y + BAR_H - 24}" stroke="hsl(var(--border))" stroke-width="1" />`;
      }
      bar += `<rect x="${tileX}" y="${iconY}" width="${iconSize}" height="${iconSize}" rx="11" fill="hsl(var(--primary))" stroke="hsl(var(--primary))" stroke-width="1" />`;
      // Real lucide icons rendered via <BarIconsOverlay/>
      bar += `<text x="${tileX}" y="${nameY}" text-anchor="start" font-size="11" font-family="Inter, system-ui, sans-serif" font-weight="700" fill="hsl(var(--primary))" letter-spacing="1.6">${col.name}</text>`;
      let curY = titleY;
      col.title.forEach((line) => {
        bar += `<text x="${tileX}" y="${curY}" text-anchor="start" font-size="15" font-family="Inter, system-ui, sans-serif" font-weight="500" fill="hsl(var(--foreground))">${line}</text>`;
        curY += 19;
      });
      curY += 3;
      col.desc.forEach((line) => {
        bar += `<text x="${tileX}" y="${curY}" text-anchor="start" font-size="12" font-family="Inter, system-ui, sans-serif" font-weight="400" fill="hsl(var(--muted-foreground))">${line}</text>`;
        curY += 18;
      });
      bar += `</g>`;
    });

    bar += `</g>`;
    svg += `<g opacity="${barOp.toFixed(3)}">${bar}</g>`;
  }

  return svg;
}

// ============================================================
// COMPONENT
// ============================================================
interface Props {
  progress: MotionValue<number>;
}

const NetworkToFoundationDiagram = ({ progress }: Props) => {
  const reduce = useReducedMotion() ?? false;
  const rootRef = useRef<SVGGElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const timeMv = useMotionValue(0);

  useEffect(() => {
    const root = rootRef.current;
    const svgEl = svgRef.current;
    if (!root || !svgEl) return;

    if (reduce) {
      root.innerHTML = renderSVG(1, 0, true);
      return;
    }

    let raf = 0;
    const loop = () => {
      const p = clamp(progress.get(), 0, 1);
      const t = performance.now() / 1000;
      timeMv.set(t);
      root.innerHTML = renderSVG(p, t, false);
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (raf) return;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) start();
        else stop();
      },
      { threshold: 0.05 }
    );
    io.observe(svgEl);

    root.innerHTML = renderSVG(progress.get(), performance.now() / 1000, false);

    return () => {
      io.disconnect();
      stop();
    };
  }, [progress, reduce, timeMv]);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
      role="img"
      aria-label="Tangled network of clinical AI workflows and integration nodes converging into the deepc AI infrastructure layer."
    >
      <defs>
        <linearGradient id="n2fConvergeFade" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--foreground))" stopOpacity="0.3" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.85" />
        </linearGradient>
        <radialGradient id="n2fCapsuleGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.45" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </radialGradient>
      </defs>
      <g ref={rootRef} />
      <BarIconsOverlay progress={progress} />
    </svg>
  );
};

// BarIconsOverlay — lucide icons positioned over the foundation bar tiles.
// Tile centers: tileX = colLeft + 24, icon center = (tileX + 20, iconY + 20).
// iconY = BAR_Y + headerH + 32 = 20 + 56 + 32 = 108, so cy = 128.
// ci=0: colLeft=80  → cx=124
// ci=1: colLeft=360 → cx=404
// ci=2: colLeft=640 → cx=684
const BAR_ICON_TILES: Array<{ Icon: LucideIcon; cx: number; cy: number; ci: number }> = [
  { Icon: Layers, cx: 124, cy: 128, ci: 0 },
  { Icon: ArrowRight, cx: 404, cy: 128, ci: 1 },
  { Icon: ShieldCheck, cx: 684, cy: 128, ci: 2 },
];

const BarIconsOverlay = ({ progress }: { progress: MotionValue<number> }) => {
  return (
    <g>
      {BAR_ICON_TILES.map(({ Icon, cx, cy, ci }) => (
        <BarIcon key={ci} Icon={Icon} cx={cx} cy={cy} ci={ci} progress={progress} />
      ))}
    </g>
  );
};

const BarIcon = ({
  Icon,
  cx,
  cy,
  ci,
  progress,
}: {
  Icon: LucideIcon;
  cx: number;
  cy: number;
  ci: number;
  progress: MotionValue<number>;
}) => {
  const start = COL_START + ci * 0.02;
  const end = start + 0.06;
  const opacity = useTransform(progress, [start, end], [0, 1], { clamp: true });
  return (
    <foreignObject x={cx - 12} y={cy - 12} width={24} height={24} style={{ pointerEvents: "none" }}>
      <motion.div style={{ display: "flex", width: 24, height: 24, opacity }}>
        <Icon size={22} color="hsl(var(--primary-foreground))" strokeWidth={2} />
      </motion.div>
    </foreignObject>
  );
};

export default NetworkToFoundationDiagram;
